import React from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Heart, Sparkles, BookOpen } from 'lucide-react';
import './Motivation.css';

const Motivation = () => {
  const { user } = useAuth();

  const motivationData = [
    {
      english: "Great job staying consistent!",
      telugu: "మంచి పని! స్థిరంగా కొనసాగుతున్నారు!",
      verse: "Hebrews 10:23",
      verseText: "Let us hold fast the profession of our faith without wavering"
    },
    {
      english: "You're growing in the Word of God!",
      telugu: "మీరు దేవుని వాక్యంలో ఎదుగుతున్నారు!",
      verse: "2 Peter 3:18",
      verseText: "But grow in grace, and in the knowledge of our Lord"
    },
    {
      english: "Don't give up — God's Word strengthens you!",
      telugu: "వదులుకోవద్దు — దేవుని వాక్యం మిమ్మల్ని బలపరుస్తుంది!",
      verse: "Psalm 119:28",
      verseText: "My soul melteth for heaviness: strengthen thou me according unto thy word"
    },
    {
      english: "Well done! Keep reading God's Word daily!",
      telugu: "బాగా చేశారు! ప్రతిరోజూ దేవుని వాక్యం చదవండి!",
      verse: "James 1:22",
      verseText: "But be ye doers of the word, and not hearers only"
    },
    {
      english: "God's Word is a lamp to your feet.",
      telugu: "దేవుని వాక్యం మీ పాదములకు దీపము.",
      verse: "Psalm 119:105",
      verseText: "Thy word is a lamp unto my feet, and a light unto my path"
    },
    {
      english: "Keep growing in Christ daily.",
      telugu: "ప్రతిరోజూ క్రీస్తులో ఎదగండి.",
      verse: "Colossians 2:6-7",
      verseText: "As ye have therefore received Christ Jesus the Lord, so walk ye in him"
    },
    {
      english: "Your faithfulness honors God!",
      telugu: "మీ విశ్వాసపూర్వకత దేవుణ్ణి గౌరవిస్తుంది!",
      verse: "1 Corinthians 4:2",
      verseText: "Moreover it is required in stewards, that a man be found faithful"
    },
    {
      english: "The Word of God is living and active!",
      telugu: "దేవుని వాక్యం సజీవమైనది మరియు శక్తివంతమైనది!",
      verse: "Hebrews 4:12",
      verseText: "For the word of God is quick, and powerful"
    },
    {
      english: "You are building your faith daily!",
      telugu: "మీరు ప్రతిరోజూ మీ విశ్వాసాన్ని నిర్మిస్తున్నారు!",
      verse: "Jude 1:20",
      verseText: "But ye, beloved, building up yourselves on your most holy faith"
    },
    {
      english: "God blesses those who seek Him!",
      telugu: "ఆయనను వెదకువారిని దేవుడు ఆశీర్వదిస్తాడు!",
      verse: "Jeremiah 29:13",
      verseText: "And ye shall seek me, and find me, when ye shall search for me with all your heart"
    }
  ];

  const biblePromises = [
    {
      title: "Strength",
      english: "I can do all things through Christ which strengtheneth me.",
      telugu: "నన్ను బలపరచు క్రీస్తు ద్వారా నేను అన్నిటిని చేయగలను.",
      verse: "Philippians 4:13"
    },
    {
      title: "Peace",
      english: "Peace I leave with you, my peace I give unto you.",
      telugu: "శాంతిని మీకు విడిచిపెట్టుచున్నాను, నా శాంతిని మీకిచ్చుచున్నాను.",
      verse: "John 14:27"
    },
    {
      title: "Guidance",
      english: "Trust in the LORD with all thine heart; and lean not unto thine own understanding.",
      telugu: "నీ పూర్ణహృదయముతో యెహోవాను నమ్ముము, నీ బుద్ధిమీద ఆధారపడకుము.",
      verse: "Proverbs 3:5"
    },
    {
      title: "Hope",
      english: "For I know the thoughts that I think toward you, saith the LORD, thoughts of peace.",
      telugu: "నేను మీ విషయమై యోచించుచున్న యోచనలు నాకు తెలియును అని యెహోవా సెలవిచ్చుచున్నాడు.",
      verse: "Jeremiah 29:11"
    }
  ];

  const motivationStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Daily Christian Motivation & Encouragement - HolyBible365",
  "description": "Find daily Christian encouragement, motivational Bible verses, and spiritual inspiration to strengthen your faith journey.",
  "url": "https://holybible365.com/motivation",
  "isPartOf": {
    "@type": "WebSite",
    "name": "HolyBible365",
    "url": "https://holybible365.com"
  },
  "mainEntity": {
    "@type": "CreativeWork",
    "name": "Christian Motivation Collection",
    "description": "Collection of motivational Bible verses and Christian encouragement",
    "inLanguage": ["en", "te"]
  }
};

return (
    <>
      <SEO 
        title="Daily Christian Motivation & Bible Encouragement"
        description="Find daily Christian encouragement, motivational Bible verses, and spiritual inspiration to strengthen your faith journey. Available in English and Telugu."
        keywords="Christian motivation, Bible encouragement, spiritual inspiration, daily verses, faith strengthening, Christian devotion"
        canonicalUrl="https://holybible365.com/motivation"
        structuredData={motivationStructuredData}
      />
      <Navbar />
      <div className="motivation-container">
        <div className="container">
          <div className="motivation-header card">
            <Heart size={48} />
            <h1>Daily Encouragement & Motivation</h1>
            <p>Let God's Word inspire and strengthen you today</p>
          </div>

          <div className="encouragement-section">
            <h2>
              <Sparkles size={24} />
              Words of Encouragement
            </h2>
            <div className="encouragement-grid">
              {motivationData.map((item, index) => (
                <div key={index} className="encouragement-card card">
                  <p className="encouragement-text">{item.english}</p>
                  {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
                    <p className="encouragement-text telugu">{item.telugu}</p>
                  )}
                  <div className="verse-info">
                    <BookOpen size={16} />
                    <span>{item.verse}</span>
                  </div>
                  <p className="verse-text">"{item.verseText}"</p>
                </div>
              ))}
            </div>
          </div>

          <div className="promises-section">
            <h2>
              <BookOpen size={24} />
              God's Promises for You
            </h2>
            <div className="promises-grid">
              {biblePromises.map((promise, index) => (
                <div key={index} className="promise-card card">
                  <h3>{promise.title}</h3>
                  <p className="promise-text">{promise.english}</p>
                  {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
                    <p className="promise-text telugu">{promise.telugu}</p>
                  )}
                  <p className="promise-verse">— {promise.verse}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="inspiration-card card">
            <h2>Keep Going! 💪</h2>
            <p>
              Every day you read God's Word, you're investing in your spiritual growth. 
              Your commitment to this 365-day journey shows your dedication to knowing God better. 
              Remember, transformation happens one day at a time!
            </p>
            {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
              <p className="telugu">
                మీరు ప్రతిరోజూ దేవుని వాక్యం చదివినప్పుడు, మీరు మీ ఆధ్యాత్మిక ఎదుగుదలలో పెట్టుబడి పెడుతున్నారు. 
                ఈ 365 రోజుల ప్రయాణానికి మీ నిబద్ధత దేవుణ్ణి బాగా తెలుసుకోవాలనే మీ అంకితభావాన్ని చూపిస్తుంది.
              </p>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Motivation;
